<div class="profile-page">
  <form autocomplete="off" [formGroup]="profileForm">
    @if (displayLoading) {
      <div
        class="d-flex flex-column align-items-center justify-content-center common-loading-container"
      >
        <div class="w-25 text-center">
          <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
          <p class="common-loading-text">Loading profile, please wait...</p>
        </div>
      </div>
    } @else {
      <div class="profile-card">
        <div class="profile-heading">
          <span class="profile-title">Profile Information</span>
          <span class="profile-subtitle">
            Manage your profile details, display name, avatar, and contact options for Fork &amp;
            Find.
          </span>
        </div>

        <div class="profile-body">
          <!-- Photo column -->
          <div class="photo-col">
            <span class="photo-label">Profile Photo</span>
            <span class="photo-hint">Recommended size: 256×256px</span>

            <div class="avatar-wrap" id="previewContainer">
              @if (imagePreview) {
                <img [src]="imagePreview" alt="Profile Preview" (error)="onImageError($event)" />
              } @else {
                <img [src]="login_user_img_url" alt="Profile Preview" />
              }
            </div>

            @if (currentUser) {
              <span class="verified-pill">Verified Admin</span>
            }

            <input
              type="file"
              #fileInput
              id="fileInput"
              class="file-input"
              accept="image/*"
              (change)="onFileSelected($event)"
            />
            <label type="button" for="fileInput" class="upload-btn">Upload Photo</label>
            <button type="button" class="remove-btn" (click)="clearProfilePhoto()">Remove</button>

            @if (imageSizeError) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>{{ imageSizeError }}</p>
              </div>
            }
          </div>

          <div class="v-divider"></div>

          <!-- Fields column -->
          <div class="fields-col">
            <div class="field">
              <label class="field-label" for="accountName">Account Name</label>
              <input
                id="accountName"
                type="text"
                class="field-input"
                formControlName="first_name"
              />
              @if (
                profileForm.get('first_name')?.errors?.['required'] &&
                (profileForm.get('first_name')?.touched || profileForm.get('first_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>First Name cannot be empty.</p>
                </div>
              } @else if (
                profileForm.get('first_name')?.errors?.['minlength'] &&
                (profileForm.get('first_name')?.touched || profileForm.get('first_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>First Name must be at least 3 characters.</p>
                </div>
              }
            </div>

            <div class="field" hidden>
              <label class="field-label">Last Name</label>
              <input type="text" class="field-input" formControlName="last_name" />
              @if (
                profileForm.get('last_name')?.errors?.['required'] &&
                (profileForm.get('last_name')?.touched || profileForm.get('last_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Last Name cannot be empty.</p>
                </div>
              } @else if (
                profileForm.get('last_name')?.errors?.['minlength'] &&
                (profileForm.get('last_name')?.touched || profileForm.get('last_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Last Name must be at least 3 characters.</p>
                </div>
              }
            </div>

            <div class="field">
              <label class="field-label" for="displayName">Display Name</label>
              <input
                id="displayName"
                type="text"
                class="field-input"
                formControlName="display_name"
                [ngClass]="{
                  'input-error':
                    profileForm.get('display_name')?.invalid &&
                    (profileForm.get('display_name')?.touched ||
                      profileForm.get('display_name')?.dirty),
                }"
              />
              @if (
                profileForm.get('display_name')?.errors?.['required'] &&
                (profileForm.get('display_name')?.touched || profileForm.get('display_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Display Name cannot be empty.</p>
                </div>
              } @else if (
                profileForm.get('display_name')?.errors?.['minlength'] &&
                (profileForm.get('display_name')?.touched || profileForm.get('display_name')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Display Name must be at least 3 characters.</p>
                </div>
              }
            </div>

            <div class="field">
              <label class="field-label" for="emailAddress">Email Address</label>
              <input
                id="emailAddress"
                type="email"
                class="field-input"
                formControlName="email"
                [disabled]="true"
              />
              @if (
                profileForm.get('email')?.errors?.['required'] &&
                (profileForm.get('email')?.touched || profileForm.get('email')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Email cannot be empty.</p>
                </div>
              } @else if (
                profileForm.get('email')?.errors?.['pattern'] &&
                (profileForm.get('email')?.touched || profileForm.get('email')?.dirty)
              ) {
                <div class="d-flex mt-2 errorText gap-2">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Invalid email format.</p>
                </div>
              }
            </div>

            <div class="field">
              <label class="field-label" for="mobileNumber">Mobile Number</label>
              <div class="phone-field">
                <span class="phone-prefix" [attr.aria-label]="phoneCountry.name">
                  <span class="phone-flag" aria-hidden="true">{{ phoneFlag }}</span>
                  +{{ phoneCountry.dialCode }}
                </span>
                <input
                  id="mobileNumber"
                  type="tel"
                  class="field-input phone-input"
                  formControlName="phone_number"
                />
              </div>
            </div>

            <div class="locked-note">
              <i class="bx bx-lock-alt"></i>
              <span>
                Contact details are locked to store domain policy. To request security access
                updates, please consult System Subscriptions settings.
              </span>
            </div>
          </div>
        </div>

        <div class="profile-footer">
          <button type="button" class="cancel-btn" (click)="resetForm()">Cancel</button>
          <button
            type="button"
            class="save-btn"
            [disabled]="profileForm.invalid || !hasProfileFormChanges()"
            (click)="onSubmit()"
          >
            Save Changes
          </button>
        </div>
      </div>
    }
  </form>
</div>
